import { format, isValid, parseISO } from 'date-fns'
import type { FactItem, FactsPayload, FactValue } from '../types'

/** Filter `app_calls.facts` jsonb into a defensively-validated FactItem[]. */
export function normalizeFacts(input: FactsPayload | null): FactItem[] {
  if (input == null) return []
  if (!Array.isArray(input.facts)) return []
  return input.facts.filter(isValidFactItem)
}

function isValidFactItem(it: unknown): it is FactItem {
  if (!it || typeof it !== 'object') return false
  const item = it as Record<string, unknown>
  if (typeof item.name !== 'string' || !item.name) return false
  if (!item.value || typeof item.value !== 'object') return false
  const val = item.value as Record<string, unknown>
  if (typeof val.kind !== 'string') return false
  // Confidence drives the dot indicator and aria-label. A missing or NaN value
  // would render "Confidence NaN%" — drop the item rather than display garbage.
  if (
    typeof item.confidence !== 'number' ||
    !Number.isFinite(item.confidence)
  ) {
    return false
  }
  // evidence_span is composed into the React row key in CallLogsSheetFacts.
  // A missing or malformed value would crash the render with a TypeError on
  // [0]/[1] access — drop the item rather than break the entire panel.
  if (
    !Array.isArray(item.evidence_span) ||
    item.evidence_span.length !== 2 ||
    typeof item.evidence_span[0] !== 'number' ||
    typeof item.evidence_span[1] !== 'number'
  ) {
    return false
  }
  return true
}

/** Render a kinded fact value for display. */
export function formatFactValue(v: FactValue): string {
  if (v.value == null || v.value === '') return '—'
  switch (v.kind) {
    case 'boolean':
      // Strict — only literal booleans become Yes/No, so a stringy 'false'
      // doesn't render as 'Yes' if upstream extraction ships malformed data.
      return v.value === true
        ? 'Yes'
        : v.value === false
          ? 'No'
          : String(v.value)
    case 'datetime': {
      if (typeof v.value !== 'string') return String(v.value)
      // Date-only ISO needs local-midnight construction to avoid TZ roll-back
      // (e.g. '1990-08-10' parsed as UTC midnight displays as Aug 9 in PT).
      // parseISO + isValid catch overflow strings like '2026-04-31' that
      // `new Date()` would silently roll forward.
      const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(v.value)
      const d = parseISO(isDateOnly ? `${v.value}T00:00:00` : v.value)
      if (!isValid(d)) return String(v.value)
      return format(d, isDateOnly ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm a')
    }
    case 'string':
    default:
      return String(v.value)
  }
}
